<template>
  <v-form>
    <v-card
      max-width="800px"
      class="mt-4 mx-auto"
      :loading="loading"
      :disabled="loading"
    >
      <v-card-text>
        <v-card-title v-text="$t('Subscription')" />
        <v-container fluid>
          <v-row>
            <v-col cols="12">
              <v-text-field
                outlined
                :error-messages="nameErrors"
                :label="$t('name')"
                @blur="$v.item.name.$touch()"
                @input="$v.item.name.$touch()"
                required
                v-model="item.name"
                :disabled="disabled"
              />
            </v-col>
            <v-col>
              <h3>{{ $t('description') }}</h3>
              <InputEditor
                v-model="item.description"
                :error-messages="descriptionErrors"
                :disabled="disabled"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-text-field
                :error-messages="priceErrors"
                :label="$t('price')"
                @blur="$v.item.price.$touch()"
                @input="$v.item.price.$touch()"
                v-model.number="item.price"
                prefix="€"
                :disabled="disabled"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <InputDate
                :error-messages="releaseDateErrors"
                :label="$t('releaseDate')"
                required
                v-model="item.releaseDate"
                @blur="$v.item.releaseDate.$touch()"
                @input="$v.item.releaseDate.$touch()"
                :disabled="disabled"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col
              cols="12"
              md="6"
            >
              <InputDate
                :error-messages="startErrors"
                :label="$t('Start')"
                required
                v-model="item.start"
                @blur="$v.item.start.$touch()"
                @input="$v.item.start.$touch()"
                :disabled="disabled"
              />
            </v-col>
            <v-col
              cols="12"
              md="6"
            >
              <InputDate
                :error-messages="endErrors"
                :label="$t('End')"
                required
                v-model="item.end"
                @blur="$v.item.end.$touch()"
                @input="$v.item.end.$touch()"
                :disabled="disabled"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-text-field
                :error-messages="subscriptionPeriodInMonthErrors"
                :label="$t('subscriptionPeriodInMonth')"
                @blur="$v.item.subscriptionPeriodInMonth.$touch()"
                @input="$v.item.subscriptionPeriodInMonth.$touch()"
                v-model.number="item.subscriptionPeriodInMonth"
                :disabled="disabled"
              />
            </v-col>
          </v-row>
          <template v-if="item.id">
            <v-row>
              <v-col>
                <DiscountListAndAdd
                  :default-item-props="{ subscription: this.item.id }"
                  :show-message="showMessage"
                  :discounts="itemDiscounts"
                  object-type="subscription"
                  @updateDiscounts="updateDiscountsHandler"
                  :is-discountable="!disabled"
                />
              </v-col>
            </v-row>
          </template>
          <template v-else>
            <v-row>
              <v-col>
                <v-alert
                  text
                  type="info"
                >{{ $t('saveSubscriptionFirst') }}</v-alert>
              </v-col>
            </v-row>
          </template>
          <v-row>
            <v-col>
              <v-select
                :label="$t('Type')"
                required
                :items="typeValues"
                v-model="item.type"
                outlined
                @change="changeContext"
                :disabled="disabled"
              />
            </v-col>
          </v-row>
          <v-row v-if="topicToggle">
            <v-col
              cols="12"
              md="8"
            >
              <v-autocomplete
                :label="$t('Topics')"
                required
                :items="availableTopics"
                v-model="currentTopics"
                item-value="element.@id"
                multiple
                chips
                :disabled="disabled"
              >
                <template
                  slot="selection"
                  slot-scope="data"
                >
                  <v-chip>{{ data.item.element.name }}</v-chip>
                </template>
                <template
                  slot="item"
                  slot-scope="data"
                >
                  <v-list-item-content :style="'padding-left: ' + topicPadding(data.item)">
                    <template v-if="data.item.hasChildren === false">
                      <v-list-item-title>
                        {{ data.item.element.name }}
                      </v-list-item-title>
                    </template>
                    <template v-else>
                      <v-list-item-title>{{ data.item.element.name }} </v-list-item-title>
                    </template>
                  </v-list-item-content>
                </template>
              </v-autocomplete>
            </v-col>
          </v-row>
          <v-row v-if="seminarToggle">
            <v-col cols="12">
              <h3>{{ $t('Seminars') }}</h3>
              <br />
              <SeminarSearch
                v-model="itemSeminars"
                :on-selected="onSeminarAcUpdated"
                :active-tenant-filter="true"
                :disabled="disabled"
              />
            </v-col>
          </v-row>
          <v-row v-if="allOnlineToggle">
            <v-col>
              <v-text-field
                :error-messages="amountOnlineErrors"
                :label="$t('IncludedInOnline')"
                @blur="$v.item.amountOnline.$touch()"
                @input="$v.item.amountOnline.$touch()"
                v-model.number="item.amountOnline"
                type="number"
                :disabled="disabled"
              />
            </v-col>
          </v-row>
          <v-row v-if="allPresenceToggle">
            <v-col>
              <v-text-field
                :error-messages="amountPresenceErrors"
                :label="$t('IncludedInPresence')"
                @blur="$v.item.amountPresence.$touch()"
                @input="$v.item.amountPresence.$touch()"
                v-model.number="item.amountPresence"
                type="number"
                :disabled="disabled"
              />
            </v-col>
          </v-row>
          <v-row v-if="allSeminarsToggle">
            <v-col>
              <v-text-field
                :error-messages="amountAllErrors"
                :label="$t('IncludedInAll')"
                @blur="$v.item.amountAll.$touch()"
                @input="$v.item.amountAll.$touch()"
                v-model.number="item.amountAll"
                type="number"
                :disabled="disabled"
              />
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
    </v-card>
  </v-form>
</template>

<script>
  import has from 'lodash/has';
  import { validationMixin } from 'vuelidate';
  import { required } from 'vuelidate/lib/validators';
  import InputEditor from '@/components/InputEditor';
  import NotificationMixin from '../../mixins/NotificationMixin';
  import { date } from '@/validators/date';
  import InputDate from '@/components/InputDate';
  import TopicMixin from '@/mixins/TopicMixin';
  import SeminarSearch from '@/components/seminar/Search';
  import SeminarMixin from '@/mixins/SeminarMixin';
  import DiscountMixin from '@/mixins/DiscountMixin';
  import DiscountListAndAdd from '@/components/discount/ListAndAdd';

  export default {
    name: 'SubscriptionForm',
    mixins: [validationMixin, NotificationMixin, TopicMixin, SeminarMixin, DiscountMixin],
    components: {
      InputEditor,
      InputDate,
      SeminarSearch,
      DiscountListAndAdd
    },
    props: {
      values: {
        type: Object,
        required: true
      },
      errors: {
        type: Object,
        default: () => { }
      },
      initialValues: {
        type: Object,
        default: () => { }
      },
      loading: {
        type: Boolean,
        required: true
      },
      refForm: {
        type: String,
        required: true
      },
      disabled: {
        type: Boolean,
        required: false,
        default: () => false,
      }
    },
    data() {
      return {
        name: null,
        description: null,
        price: null,
        releaseDate: null,
        seminarToggle: false,
        topicToggle: false,
        allPresenceToggle: false,
        allOnlineToggle: false,
        allSeminarsToggle: false,
        subscriptionPeriodInMonth: 12,
        typeValues: [
          { text: this.$t('All'), value: 'all' },
          { text: this.$t('XSeminars'), value: 'seminars_x' },
          { text: this.$t('XOnline'), value: 'online_x' },
          { text: this.$t('XPresence'), value: 'presence_x' },
          { text: this.$t('XOnlineXPresence'), value: 'online_x_presence_x' },
          { text: this.$t('AllOnline'), value: 'online' },
          { text: this.$t('AllPresence'), value: 'presence' },
          { text: this.$t('AllOnlineXPresence'), value: 'all_online_x_presence' },
          { text: this.$t('AllPresenceXOnline'), value: 'all_presence_x_online' },
          { text: this.$t('SpecificTopics'), value: 'topics' },
          { text: this.$t('SpecificSeminars'), value: 'seminars' },
        ]
      };
    },
    methods: {
      onSeminarAcUpdated(selectedSeminars) {
        this.item.seminars.collection = selectedSeminars;
      },
      changeContext(type) {
        switch (type) {
          case 'seminars':
            this.untoggleAll();
            this.seminarToggle = true;
            break;
          case 'topics':
            this.untoggleAll();
            this.topicToggle = true;
            break;
          case 'all_online_x_presence':
            this.untoggleAll();
            this.allOnlineToggle = true;
            break;
          case 'all_presence_x_online':
            this.untoggleAll();
            this.allPresenceToggle = true;
            break;
          case 'online_x':
            this.untoggleAll();
            this.allOnlineToggle = true;
            break;
          case 'presence_x':
            this.untoggleAll();
            this.allPresenceToggle = true;
            break;
          case 'seminars_x':
            this.untoggleAll();
            this.allSeminarsToggle = true;
            break;
          case 'online_x_presence_x':
            this.untoggleAll();
            this.allOnlineToggle = true;
            this.allPresenceToggle = true;
            break;
          default:
            this.untoggleAll();
            break;
        }
      },
      untoggleAll() {
        this.topicToggle = false;
        this.seminarToggle = false;
        this.allPresenceToggle = false;
        this.allOnlineToggle = false;
        this.seminarToggle = false;
        this.allSeminarsToggle = false;
      },
      updateDiscountsHandler(updatedDiscounts) {
        this.item.discounts.collection = updatedDiscounts;
      },
    },
    computed: {
      item() {
        return this.initialValues || this.values;
      },
      //region validation:
      nameErrors() {
        const errors = [];

        if (!this.$v.item.name.$dirty) {
          return errors;
        }

        has(this.violations, 'name') && errors.push(this.violations.name);

        !this.$v.item.name.required && errors.push(this.$t('Field is required'));

        return errors;
      },
      descriptionErrors() {
        const errors = [];

        if (!this.$v.item.description.$dirty) {
          return errors;
        }

        has(this.violations, 'description') && errors.push(this.violations.description);
        return errors;
      },
      priceErrors() {
        const errors = [];

        if (!this.$v.item.price.$dirty) {
          return errors;
        }

        has(this.violations, 'price') && errors.push(this.violations.price);

        !this.$v.item.price.required && errors.push(this.$t('Field is required'));

        return errors;
      },
      releaseDateErrors() {
        const errors = [];
        if (!this.$v.item.releaseDate.$dirty) {
          return errors;
        }

        has(this.violations, 'releaseDate') && errors.push(this.violations.releaseDate);

        !this.$v.item.releaseDate.required && errors.push(this.$t('Field is required'));

        return errors;
      },
      startErrors() {
        const errors = [];
        if (!this.$v.item.start.$dirty) {
          return errors;
        }

        has(this.violations, 'start') && errors.push(this.violations.start);

        !this.$v.item.start.required && errors.push(this.$t('Field is required'));

        return errors;
      },
      endErrors() {
        const errors = [];
        if (!this.$v.item.end.$dirty) {
          return errors;
        }

        has(this.violations, 'end') && errors.push(this.violations.end);

        !this.$v.item.end.required && errors.push(this.$t('Field is required'));

        return errors;
      },
      subscriptionPeriodInMonthErrors() {
        const errors = [];
        if (!this.$v.item.subscriptionPeriodInMonth.$dirty) {
          return errors;
        }

        has(this.violations, 'subscriptionPeriodInMonth') && errors.push(this.violations.subscriptionPeriodInMonth);

        !this.$v.item.subscriptionPeriodInMonth.required && errors.push(this.$t('Field is required'));

        return errors;
      },
      amountPresenceErrors() {
        const errors = [];
        if (!this.$v.item.amountPresence.$dirty) {
          return errors;
        }

        has(this.violations, 'amountPresence') && errors.push(this.violations.amountPresence);

        return errors;
      },
      amountOnlineErrors() {
        const errors = [];
        if (!this.$v.item.amountOnline.$dirty) {
          return errors;
        }

        has(this.violations, 'amountOnline') && errors.push(this.violations.amountOnline);

        return errors;
      },
      amountAllErrors() {
        const errors = [];
        if (!this.$v.item.amountAll.$dirty) {
          return errors;
        }

        has(this.violations, 'amountAll') && errors.push(this.violations.amountAll);

        return errors;
      },
      currentTopics: {
        get: function () {
          return this.item.topics ? this.item.topics.collection.map((t) => (t.id)) : null;
        },
        set: function (val) {
          this.item.topics.collection = val;
        }
      },
      availableTopics() {
        return this.groupedTopics;
      },
      itemSeminars: {
        get: function () {
          if(this.item.seminars.collection === undefined) {
            return [];
          }
          const resolvedSeminars = this.resolveSeminars(this.item.seminars.collection.map((seminar) => {
            if (typeof seminar === 'string' || seminar instanceof String) {
              return seminar
            } else {
              return seminar.id
            }
          }));
          if (resolvedSeminars.length === this.item.seminars.collection.length) {
            return resolvedSeminars;
          }
          return [];
        },
        set: function (newValue) {
          this.item.seminars.collection = newValue;
        }
      },
      itemDiscounts() {
        return this.item.discounts.collection.map((discount) => {
          if (typeof discount === 'string' || discount instanceof String) {
            return discount
          } else {
            return discount.id
          }
        })
      }
    },
    validations: {
      item: {
        name: {
          required
        },
        description: {
          required
        },
        price: {
          required
        },
        releaseDate: {
          required,
          date
        },
        start: {
          required,
          date
        },
        end: {
          required,
          date
        },
        subscriptionPeriodInMonth: {
          required,
        },
        amountOnline: {},
        amountPresence: {},
        amountAll: {},
      }
    },
    mounted() {
      this.changeContext(this.item.type)
    }
  };
</script>
